import { useQuery } from "react-query";

import axios, { AxiosError } from "axios";

import { GeomBinListItem, USER_COLUMN_RS_ENDPOINT } from "api/data";

import { EntityKind } from "models/entityKind";

import { IGroupByListColumn } from "../models";

export const ALL_GEOM_BINS_QUERY_KEY = "allGeomBins";

const GeomBinPrefix = "GeomBin";
const UserDefinedColumnPropertyPrefix = "My_Focus_Fields";
const OrgDefinedColumnPropertyPrefix = "Org_Focus_Fields";
const ProjectDefinedColumnPropertyPrefix = "Project_Focus_Fields";

export interface UseAllGeomBinsQueryOptions {
  entityKind?: EntityKind;
  selectedProjectId?: string;
  onError?: (error: AxiosError) => void;
  onSuccess?: (data: AllGeomBinsData) => void;
  enabled?: boolean;
}

export interface AllGeomBinsData {
  userGeomBins: IGroupByListColumn[];
  orgGeomBins: IGroupByListColumn[];
  projectGeomBins: IGroupByListColumn[];
  allGeomBins: IGroupByListColumn[];
}

export interface UseAllGeomBinsQueryResult {
  data: AllGeomBinsData | undefined;
  error: AxiosError;
  isError: boolean;
  isIdle: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  refetch: () => void;
}

const fetchUserGeomBins = async (
  entityKind: EntityKind
): Promise<IGroupByListColumn[]> => {
  if (entityKind !== EntityKind.Well) {
    return [];
  }

  const userGeomBinsResponse = await axios.get(
    `${USER_COLUMN_RS_ENDPOINT}/geombins?isOrg=false`
  );

  if (userGeomBinsResponse.status === 200 && userGeomBinsResponse.data) {
    return userGeomBinsResponse.data.map(
      (bin: GeomBinListItem) =>
        ({
          title: bin.name,
          id: bin.id,
          canBin: false,
          dataType: "Text",
          canNormalize: false,
          group: GeomBinPrefix,
          subgroup: bin.group,
          property: `${UserDefinedColumnPropertyPrefix}_${GeomBinPrefix}_${btoa(
            bin.group
          )}_${bin.id}`,
          withinSubgroupOrder: bin.withinSubGroupOrder,
          fieldType: "geom"
        } as IGroupByListColumn)
    );
  }

  return [];
};

const fetchOrgGeomBins = async (
  entityKind: EntityKind
): Promise<IGroupByListColumn[]> => {
  if (entityKind !== EntityKind.Well) {
    return [];
  }

  const orgGeomBinsResponse = await axios.get(
    `${USER_COLUMN_RS_ENDPOINT}/geombins?isOrg=true`
  );

  if (orgGeomBinsResponse.status === 200 && orgGeomBinsResponse.data) {
    return orgGeomBinsResponse.data.map(
      (bin: GeomBinListItem) =>
        ({
          title: bin.name,
          id: bin.id,
          canBin: false,
          dataType: "Text",
          canNormalize: false,
          group: GeomBinPrefix,
          subgroup: bin.group,
          property: `${OrgDefinedColumnPropertyPrefix}_${GeomBinPrefix}_${btoa(
            bin.group
          )}_${bin.id}`,
          fieldType: "geom",
          withinSubgroupOrder: bin.withinSubGroupOrder
        } as IGroupByListColumn)
    );
  }

  return [];
};

const fetchProjectGeomBins = async (
  entityKind: EntityKind,
  selectedProjectId?: string
): Promise<IGroupByListColumn[]> => {
  if (entityKind !== EntityKind.Well || !selectedProjectId) {
    return [];
  }

  const projectGeomBinsResponse = await axios.get(
    `${USER_COLUMN_RS_ENDPOINT}/geombins?projectId=${selectedProjectId}`
  );

  if (projectGeomBinsResponse.status === 200 && projectGeomBinsResponse.data) {
    return projectGeomBinsResponse.data.map(
      (bin: GeomBinListItem) =>
        ({
          title: bin.name,
          id: bin.id,
          canBin: false,
          dataType: "Text",
          canNormalize: false,
          group: GeomBinPrefix,
          subgroup: bin.group,
          property: `${ProjectDefinedColumnPropertyPrefix}_${GeomBinPrefix}_${btoa(
            bin.group
          )}_${bin.id}`,
          withinSubgroupOrder: bin.withinSubGroupOrder,
          fieldType: "geom"
        } as IGroupByListColumn)
    );
  }

  return [];
};

const fetchAllGeomBins = async (
  entityKind: EntityKind,
  selectedProjectId?: string
): Promise<AllGeomBinsData> => {
  const [userGeomBins, orgGeomBins, projectGeomBins] = await Promise.allSettled([
    fetchUserGeomBins(entityKind),
    fetchOrgGeomBins(entityKind),
    fetchProjectGeomBins(entityKind, selectedProjectId)
  ]);

  const userBins = userGeomBins.status === "fulfilled" ? userGeomBins.value : [];
  const orgBins = orgGeomBins.status === "fulfilled" ? orgGeomBins.value : [];
  const projectBins = projectGeomBins.status === "fulfilled" ? projectGeomBins.value : [];

  const allGeomBins = [...userBins, ...orgBins, ...projectBins];

  return {
    userGeomBins: userBins,
    orgGeomBins: orgBins,
    projectGeomBins: projectBins,
    allGeomBins
  };
};

export function useAllGeomBinsQuery({
  entityKind = EntityKind.Well,
  selectedProjectId,
  onError,
  onSuccess,
  enabled = true
}: UseAllGeomBinsQueryOptions = {}): UseAllGeomBinsQueryResult {
  const queryInfo = useQuery({
    queryKey: [ALL_GEOM_BINS_QUERY_KEY, entityKind, selectedProjectId],
    queryFn: () => fetchAllGeomBins(entityKind, selectedProjectId),
    onError: (error: AxiosError) => {
      onError && onError(error);
    },
    onSuccess: (data) => {
      onSuccess && onSuccess(data);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000 // 30 minutes
  });

  return {
    data: queryInfo.data,
    error: queryInfo.error,
    isError: queryInfo.status === "error",
    isIdle: queryInfo.status === "idle",
    isLoading: queryInfo.status === "loading",
    isSuccess: queryInfo.status === "success",
    refetch: queryInfo.refetch
  };
}
